import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { AppActions } from "../../../../store/ducks/app";
import { AtribuirActions } from "../../../../store/ducks/atribuir";
import { DashBoardActions } from "../../../../store/ducks/dashboard";
import {
	OperationalDashboardActions,
	TodayFocusActionKind,
	TodayFocusItem,
	TodayFocusQueueKey,
} from "../../../../store/ducks/operationalDashboard";
import { handlePaciente } from "../../../../utils/funcs";
import { createIdempotencyKey } from "../../services/patientContactService";
import { todayFocusItemIdentity } from "../../services/todayFocusService";

export interface TodayFocusCardView {
	queueKey: TodayFocusQueueKey;
	item: TodayFocusItem;
	name: string;
	description: string;
	cta: string;
	loading: boolean;
	completion: "success" | "dismissed" | null;
	error: string | null;
}

const queueOrder: TodayFocusQueueKey[] = [
	"compartilharRelatorio",
	"convidarReavaliacao",
	"resgatarCliente",
];

const cardCopy = (
	queueKey: TodayFocusQueueKey,
	item: TodayFocusItem
): Pick<TodayFocusCardView, "description" | "cta"> => {
	if (queueKey === "compartilharRelatorio") {
		return {
			description: "Relatório pronto para ser compartilhado.",
			cta: "Compartilhar relatório",
		};
	}

	const evaluation = moment(item.ULTIMA_AVALIACAO?.DATA_HORA);
	const days = evaluation.isValid()
		? Math.max(
				moment().startOf("day").diff(evaluation.startOf("day"), "days"),
				0
		  )
		: null;
	const elapsed =
		days === null
			? "Última avaliação disponível"
			: days === 1
			? "Última avaliação há 1 dia"
			: `Última avaliação há ${days} dias`;

	return queueKey === "convidarReavaliacao"
		? {
				description: `${elapsed}. Convide o paciente para uma reavaliação.`,
				cta: "Convidar para reavaliação",
		  }
		: {
				description: `${elapsed}. Retome o contato com este cliente.`,
				cta: "Retomar contato",
		  };
};

export const useTodayFocusViewModel = (
	onOpenContact?: (queueKey: TodayFocusQueueKey, item: TodayFocusItem) => void
) => {
	const dispatch = useDispatch();
	const focus = useSelector(
		(state: any) => state?.operationalDashboard?.todayFocus
	);
	const configServices = useSelector(
		(state: any) => state?.configs?.config_servicos
	);
	const currentLanguage = useSelector(
		(state: any) => state?.configs?.current_language
	);
	const [localErrors, setLocalErrors] = useState<
		Partial<Record<TodayFocusQueueKey, string>>
	>({});
	const inFlight = useRef<Partial<Record<TodayFocusQueueKey, boolean>>>({});
	const mounted = useRef(true);

	useEffect(() => {
		mounted.current = true;
		dispatch(OperationalDashboardActions.today_focus_request());
		return () => {
			mounted.current = false;
			inFlight.current = {};
		};
	}, [dispatch]);

	const cards = useMemo<TodayFocusCardView[]>(() => {
		if (!focus?.queues) return [];
		return queueOrder.flatMap((queueKey) => {
			const item = focus.queues[queueKey]?.items?.[0] as
				| TodayFocusItem
				| undefined;
			if (!item) return [];
			const copy = cardCopy(queueKey, item);
			return [
				{
					queueKey,
					item,
					name: item.USUARIO_NOME || "Paciente",
					...copy,
					loading: !!focus.actions?.[queueKey]?.loading,
					completion: focus.actions?.[queueKey]?.completion || null,
					error:
						localErrors[queueKey] || focus.actions?.[queueKey]?.error || null,
				},
			];
		});
	}, [focus?.queues, focus?.actions, localErrors]);

	const idempotencyKeyFor = useCallback(
		(
			queueKey: TodayFocusQueueKey,
			item: TodayFocusItem,
			actionKind: TodayFocusActionKind
		) => {
			const action = focus?.actions?.[queueKey];
			const identity = todayFocusItemIdentity(item);
			return action?.error &&
				action.itemIdentity === identity &&
				action.actionKind === actionKind &&
				action.idempotencyKey
				? action.idempotencyKey
				: createIdempotencyKey();
		},
		[focus?.actions]
	);

	const dispatchAction = useCallback(
		(
			queueKey: TodayFocusQueueKey,
			item: TodayFocusItem,
			actionKind: TodayFocusActionKind,
			onSuccess?: (result?: any) => void,
			onFailure?: () => void
		) => {
			if (inFlight.current[queueKey] || focus?.actions?.[queueKey]?.loading)
				return false;
			inFlight.current[queueKey] = true;
			setLocalErrors((current) => ({ ...current, [queueKey]: undefined }));
			dispatch(
				OperationalDashboardActions.today_focus_action_request({
					queueKey,
					item,
					actionKind,
					idempotencyKey: idempotencyKeyFor(queueKey, item, actionKind),
					onSuccess: (result) => {
						inFlight.current[queueKey] = false;
						onSuccess?.(result);
					},
					onFailure: () => {
						inFlight.current[queueKey] = false;
						onFailure?.();
					},
				})
			);
			return true;
		},
		[dispatch, focus?.actions, idempotencyKeyFor]
	);

	const handlePrimaryAction = useCallback(
		(card: TodayFocusCardView) => {
			if (card.loading) return;
			if (card.queueKey === "compartilharRelatorio") {
				dispatchAction(
					card.queueKey,
					card.item,
					"REPORT_SHARE_STARTED",
					(fullPatient) => {
						if (!mounted.current) return;
						handlePaciente(fullPatient, dispatch, configServices);
						dispatch(AtribuirActions.set_menu_option_selected(""));
						dispatch(AppActions.set_lista_pacientes([]));
						dispatch(DashBoardActions.set_current_page("emissao_relatorios"));
						dispatch(
							AppActions.set_option_menu(
								false,
								currentLanguage?.menu_lateral_inicio
							)
						);
					}
				);
				return;
			}

			onOpenContact?.(card.queueKey, card.item);
		},
		[
			configServices,
			currentLanguage?.menu_lateral_inicio,
			dispatch,
			dispatchAction,
			onOpenContact,
		]
	);

	const handleDismiss = useCallback(
		(card: TodayFocusCardView) => {
			if (card.loading) return;
			dispatchAction(card.queueKey, card.item, "DISMISSED");
		},
		[dispatchAction]
	);

	return {
		cards,
		isInitialLoading: !!focus?.loading && !focus?.loaded,
		isRefreshing: !!focus?.loading && !!focus?.loaded,
		isLoaded: !!focus?.loaded,
		error: focus?.error || null,
		retry: () => dispatch(OperationalDashboardActions.today_focus_request()),
		handlePrimaryAction,
		handleDismiss,
	};
};
